import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import React, { useEffect } from 'react';
import { clsx } from 'clsx';
import { ExpandCollapseIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/ExpandCollapseIcon/ExpandCollapseIcon';
import { CheckIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/CheckIcon/CheckIcon';
import { AddItemIcon } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/AddItemIcon/AddItemIcon';
import { Separator } from 'src/components/ui/separator';
import { UploadFileItem } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/UploadFilesAccordion/UploadFileItem';
import { AddItemIconDisabled } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep2/AddItemIcon/AddItemIconDisabled';
import type { KnowledgeBase } from 'src/services/knowledgeBaseService';
import type { KBFile } from 'src/utils/fileUtils';

export const UploadFilesAccordion = ({
  items = [],
  upload = () => {},
  isButtonsDisabled = false,
  knowledgeBase,
}: {
  items?: KBFile[];
  // Opens the file picker.
  upload?: () => void;
  isButtonsDisabled?: boolean;
  knowledgeBase: Pick<KnowledgeBase, 'id'>;
}) => {
  const disabled = !items.length;
  const [value, setValue] = React.useState('');
  const [firstExpanded, setFirstExpanded] = React.useState(false);

  useEffect(() => {
    if (items.length && !firstExpanded && !value) {
      setFirstExpanded(true);
      setValue('new-card');
    }
  }, [items]);

  return (
    <div className={'w-full flex flex-row items-start gap-5'}>
      <div className={'pt-5'}>
        <CheckIcon className={clsx(!disabled ? 'visible' : 'invisible')} />
      </div>
      <Accordion
        value={value}
        onValueChange={setValue}
        type="single"
        className="w-full max-w-[455px] min-w-[455px]"
        collapsible={true}
        disabled={disabled}
      >
        <AccordionItem
          value="new-card"
          className={clsx(
            'group border border-[#B8C1C633] rounded-sm-5 bg-white transition-shadow shadow-none',
            !disabled && 'hover:shadow-[0_2px_2px_rgba(0,0,0,0.25)]',
          )}
        >
          <AccordionTrigger hideDefaultIcon className="py-2.5 px-5">
            <div className="flex items-center justify-between w-full">
              <div className={'flex flex-row gap-[7px]'}>
                <div className={'pt-[3px]'}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="11"
                    height="15"
                    viewBox="0 0 11 15"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.59037 1.1523e-06C6.91319 8.234e-05 7.22569 0.116987 7.47312 0.330237L7.5625 0.414387L10.5971 3.53572C10.8254 3.7705 10.9661 4.08041 10.9945 4.41116L11 4.53561L11 12.7286C11.0001 13.0854 10.8691 13.429 10.6332 13.6907C10.3973 13.9523 10.074 14.1126 9.72812 14.1393L9.625 14.1429L1.375 14.1429C1.0281 14.143 0.693983 14.0082 0.439621 13.7656C0.185259 13.523 0.0294525 13.1904 0.0034354 12.8346L-2.1566e-06 12.7286L-1.78347e-07 1.41429C-0.000109896 1.05748 0.130904 0.713813 0.366779 0.452184C0.602654 0.190554 0.925956 0.0302965 1.27188 0.00353602L1.375 2.40413e-07L6.59037 1.1523e-06ZM5.5 1.41429L1.375 1.41429L1.375 12.7286L9.625 12.7286L9.625 5.65714L6.53125 5.65714C6.27489 5.65713 6.02773 5.55891 5.83796 5.38163C5.64819 5.20435 5.52942 4.96072 5.50481 4.69826L5.5 4.59643L5.5 1.41429ZM6.875 1.70704L6.875 4.24286L9.34037 4.24286L6.875 1.70704Z"
                      fill="#05050D"
                    />
                  </svg>
                </div>
                <div className={'text-start'}>
                  <p className={'text-ebony text-sm font-semibold'}>
                    Upload files{' '}
                    <span
                      className={clsx(
                        'text-base-xs font-medium text-[#7A6BE5] bg-[#A498FF1A] px-[9px] py-[2px] rounded-[50px] ml-2.5',
                        disabled && 'invisible',
                      )}
                    >
                      {items.length}
                    </span>
                  </p>
                  <p className={'text-ebony_opacity_30 text-base-xs font-medium'}>
                    PDFs, docs, spreadsheets… whatever holds the info
                  </p>
                </div>
              </div>
              {!disabled && (
                <ExpandCollapseIcon
                  className={clsx(
                    'w-[12px] opacity-50 rotate-0 transition group-hover:opacity-100',
                    value && 'rotate-180',
                  )}
                />
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-0">
            {items.map((item, index, array) => {
              return (
                <div key={`item-${item.id}-${item.name}`}>
                  <UploadFileItem item={item} knowledgeBase={knowledgeBase} />
                  {index !== array.length - 1 && (
                    <Separator className={'h-[1px] bg-[#b8c1c657] -mx-5'} />
                  )}
                </div>
              );
            })}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <button
        type="button"
        aria-label="Upload files"
        onClick={() => {
          if (isButtonsDisabled) return;

          upload();
        }}
        className={clsx('group hover:cursor-pointer pt-3.5')}
      >
        {isButtonsDisabled ? <AddItemIconDisabled /> : <AddItemIcon />}
      </button>
    </div>
  );
};
